/**
 * KAN-940: автосохранение текстового поля (комментарий в блоке "Итог"
 * приложения менеджера). Сохраняет через delayMs после последней правки,
 * сразу при уходе из поля (onBlur) и при размонтировании, если есть
 * несохранённая правка. Последний сохранённый текст хук помнит сам: тот же
 * текст повторно не уходит. Запросы идут по одному: правка во время запроса
 * уходит следующим запросом после ответа (старый ответ не перетирает новый).
 * Ошибка оставляет текст, status "error" (с текстом ошибки, если он есть),
 * retry сохраняет снова; правка обратно к сохранённому снимает ошибку.
 * Новое значение с сервера (saved) показывается только без несохранённых правок.
 * Сбой сохранения после размонтирования: onFailAfterUnmount (например, тост).
 * KAN-944: с draftKey несохранённый текст лежит черновиком (lib/composerDraft),
 * пока сохранение не прошло; сохранённый или совпавший с сервером убирается.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { writeComposerDraft } from '@/lib/composerDraft';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Результат сохранения: true или null - успех; false или текст ошибки - сбой. */
export type AutosaveResult = boolean | string | null;

interface UseAutosaveTextOptions {
  /** Значение с сервера. */
  saved: string;
  save: (text: string) => Promise<AutosaveResult>;
  delayMs?: number;
  /** Сохранение не удалось, а компонента уже нет на экране. */
  onFailAfterUnmount?: () => void;
  /** KAN-944: ключ черновика несохранённого текста. */
  draftKey?: string;
}

export function useAutosaveText({ saved, save, delayMs = 1500, onFailAfterUnmount, draftKey }: UseAutosaveTextOptions) {
  const [value, setValueState] = useState(saved);
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const valueRef = useRef(saved);
  const lastSavedRef = useRef(saved);
  const inFlightRef = useRef(false);
  const requestedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const saveRef = useRef(save);
  const onFailRef = useRef(onFailAfterUnmount);
  const draftKeyRef = useRef(draftKey);
  useEffect(() => {
    saveRef.current = save;
    onFailRef.current = onFailAfterUnmount;
    draftKeyRef.current = draftKey;
  });
  // Черновик: несохранённый текст или ничего.
  const syncDraft = useCallback(() => {
    const text = valueRef.current;
    writeComposerDraft(draftKeyRef.current, text === lastSavedRef.current ? '' : text);
  }, []);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const flush = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      // Идёт запрос: после ответа уйдёт последний текст.
      requestedRef.current = true;
      return;
    }
    const text = valueRef.current;
    if (text === lastSavedRef.current) return;
    inFlightRef.current = true;
    requestedRef.current = false;
    if (mountedRef.current) setStatus('saving');
    let result: AutosaveResult;
    try {
      result = await saveRef.current(text);
    } catch {
      result = false;
    }
    inFlightRef.current = false;
    const ok = result === true || result === null;
    if (ok) {
      lastSavedRef.current = text;
      syncDraft();
    }
    if (!mountedRef.current) {
      if (!ok) onFailRef.current?.();
    } else if (ok) {
      setError(null);
      setStatus(valueRef.current === text ? 'saved' : 'idle');
    } else {
      setError(typeof result === 'string' ? result : null);
      setStatus('error');
    }
    if (requestedRef.current) {
      requestedRef.current = false;
      if (valueRef.current !== lastSavedRef.current) void flush();
    }
  }, [syncDraft]);

  const setValue = useCallback(
    (next: string) => {
      valueRef.current = next;
      setValueState(next);
      clearTimer();
      syncDraft();
      if (next === lastSavedRef.current) {
        // Вернули сохранённый текст: ошибка больше не актуальна.
        setStatus((s) => (s === 'error' ? 'idle' : s));
        setError(null);
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void flush();
      }, delayMs);
    },
    [delayMs, flush, syncDraft]
  );

  const saveNow = useCallback(() => {
    clearTimer();
    void flush();
  }, [flush]);

  // Значение с сервера: подхватить, только если несохранённых правок нет.
  useEffect(() => {
    if (saved === lastSavedRef.current) return;
    if (valueRef.current !== lastSavedRef.current) return;
    lastSavedRef.current = saved;
    valueRef.current = saved;
    setValueState(saved);
    syncDraft();
  }, [saved, syncDraft]);

  // Открыли с сохранённым текстом: черновик от прошлого раза не нужен.
  useEffect(() => {
    syncDraft();
  }, [syncDraft, draftKey]);

  // Ушли с экрана с несохранённой правкой: сохранить сразу, без setState.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      void flush();
    };
  }, [flush]);

  return { value, setValue, onBlur: saveNow, status, error, retry: saveNow };
}
